/**
 * How many more cars this machine room may be given — spec §4 rule 1, moved
 * from a verdict to a limit.
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 * From the design authority, running Test Survey 5 on site, 2026-08-07:
 *
 *   "I'm able to add seven more cars… even though at the beginning I said there
 *    were only five elevators in the building. We should not be allowed to keep
 *    adding elevator units."
 *
 * Rule 1 already knows: the cars across every machine room must total B11. But
 * it only FLAGS, and D-014 correctly keeps that flag calm while setup is in
 * progress — so nothing at all stood between a five-elevator building and a
 * twelfth car. The app knew and would not say (D-015/D-016). This module lets
 * the screen say it at the tap, before the extra car exists, instead of
 * reporting it afterwards.
 *
 * ── Prevent, but leave a door ────────────────────────────────────────────────
 * A hard block would assume B11 is always right. The surveyor is the one
 * actually standing in the building: if the office recorded 5 and there are
 * genuinely 7, a hard stop strands them with no way to record reality. So the
 * budget is a limit the surveyor can RAISE — `raiseBuildingTotal` writes B11
 * through the ordinary answer path, so the same validation, the same gap engine
 * and the same cross-check chip all see it. Nothing here bypasses a rule.
 *
 * ── This module decides nothing arithmetical of its own ──────────────────────
 * Every number comes from `unitCountCrossCheck()` in src/core/calc.ts — the same
 * function §4 rule 1 and the building screen's chip read. The budget is that
 * arithmetic rearranged ("what is left" rather than "do these agree"), never a
 * second implementation of it.
 */

import { unitCountCrossCheck } from '../../core/calc';
import type { CarRef, SurveyDocument } from '../../core/types';

/** What the room setup screen needs to know before drawing "+ Add car". */
export interface CarBudget {
  /** B11, when it has been answered. Undefined on a bare-address survey (§2.1). */
  buildingTotal?: number;
  /** Cars already on this room's list. */
  here: number;
  /** Cars assigned in every OTHER machine room. */
  elsewhere: number;
  /**
   * Cars this room may still be given — `buildingTotal − elsewhere − here`,
   * floored at zero. **Undefined when there is no building total**, which is not
   * the same as zero: nothing has told the form what to expect, so nothing is
   * limited.
   */
  remaining?: number;
  /** Whether "+ Add car" may be offered at all. True whenever B11 is unanswered. */
  canAdd: boolean;
}

/**
 * The remaining car budget for one machine room.
 *
 * The budget is B11 minus the cars in the OTHER rooms — this room's own list is
 * then measured against it. That is what makes the limit correct while several
 * rooms are being built in any order, and what makes removing a car in Machine
 * Room 1 hand the budget straight back to Machine Room 2.
 */
export function carBudget(doc: SurveyDocument, roomId: string): CarBudget {
  const check = unitCountCrossCheck(doc);
  const here = check.perRoom.find((entry) => entry.roomId === roomId)?.units ?? 0;
  const elsewhere = check.roomTotal - here;
  const total = check.buildingTotal;

  // §2.1: a survey may arrive as bare as an address. No total, no limit — and
  // no sentence either, because there is nothing true to say yet.
  if (total === undefined) return { here, elsewhere, canAdd: true };

  const remaining = Math.max(0, total - elsewhere - here);

  return {
    buildingTotal: total,
    here,
    elsewhere,
    remaining,
    canAdd: remaining > 0,
  };
}

/*
 * `budgetMessage` — the standing sentence under the car list — retired with
 * D-030: the car rows are the count. The budget (the limit spent at the
 * tap) is unchanged.
 */

/**
 * Add a car, but only if the budget allows it.
 *
 * Mutates the document in place, so the screen calls it INSIDE `setStructure`'s
 * transaction and the budget is re-checked against the document as it is at
 * write time — not against the render that drew the button. Two taps in flight
 * on a 44px control cannot slip a sixth car into a five-elevator building.
 *
 * Returns whether the car was added, so a caller can tell a refusal from a
 * missing room.
 */
export function addCarWithinBudget(doc: SurveyDocument, roomId: string, car: CarRef): boolean {
  const room = doc.rooms.find((candidate) => candidate.id === roomId);
  if (!room) return false;
  if (!carBudget(doc, roomId).canAdd) return false;
  room.cars.push(car);
  return true;
}

/*
 * `totalCorrection` — the fix-the-total stepper inside Room Setup — retired
 * with D-030: B11 is edited on the Building screen, and the settled V1
 * reconciliation catches a mismatch at close.
 */
